// utils
import { codeGeneretor, toMinutes } from 'app/utils';
import { differenceInDays } from 'date-fns';
// config
import { PAD_ITEM_TYPE } from 'app/config/pad_item.js';

//class
import PadCreator from './PadCreator';

const STATUS = {
  ACTIVE: 1,
  INACTIVE: 0
};

/**
 * represents an instance of PadCreatorMatMed
 * @class PadCreatorMatMed
 * @extends PadCreator
 */
export default class PadCreatorMatMed extends PadCreator {
  prepare(matMeds = [], prescriptions = [], padDetails) {
    const preparedMatMeds = matMeds.map(matMed => ({
      id: matMed.id,
      code: codeGeneretor(),
      amount: matMed.amount,
      type: matMed.type,
      itemId: matMed.itemId,
      unityPrice: matMed.unityPrice,
      name: matMed.matMed.name,
      status: matMed.status
    }));

    const preparedMatMedsToScreen = [];

    preparedMatMeds.forEach(matMed => {
      preparedMatMedsToScreen.push({ ...matMed, amount: 0 });
    });

    this._loadedData = preparedMatMeds;
    this._screenData = preparedMatMedsToScreen;

    prescriptions.forEach(prescription => {
      this.insert(prescription, padDetails);
    });

    return this;
  }

  // INSERT
  insert(prescription, padDetails) {
    const { medicamentId, dietId, procedureId } = prescription;

    const isMedicament = !!medicamentId;
    const isDiet = !!dietId;
    const isProcedure = !!procedureId;

    prescription = this._setPeriodInMinutesInThePrescription(prescription, padDetails);

    if (isDiet || isMedicament) {
      this._insertFromMedicamentOrDiet(prescription);
    }

    if (isProcedure) {
      this._insertFromProcedure(prescription);
    }

    return this;
  }
  _insertFromMedicamentOrDiet(prescription) {
    const {
      medicamentId,
      medicament,
      diet,
      dietId,
      once,
      periodInMinutes,
      dose: amountMatMed
    } = prescription;

    const itemId = medicamentId || dietId;

    const frequency = this._getFrequency(prescription);

    const name = medicamentId ? medicament.name : diet.name;

    const matMedDetails = {
      once,
      periodInMinutes,
      frequency
    };

    const matMed = {
      name,
      amountMatMed,
      itemId
    };

    this._processInsert(matMed, matMedDetails);
  }
  _insertFromProcedure(prescription) {
    const { padPrescriptionMatMeds, kits, once, periodInMinutes } = prescription;

    const frequency = this._getFrequency(prescription);

    const matMedDetails = {
      once,
      periodInMinutes,
      frequency
    };

    !!kits &&
      kits.forEach(({ matMeds }) => {
        matMeds.forEach(matMed => {
          const { name, amount: amountMatMed, id: itemId } = matMed;

          const matMedToAdd = {
            name,
            amountMatMed,
            itemId
          };

          this._processInsert(matMedToAdd, matMedDetails);
        });
      });

    !!padPrescriptionMatMeds &&
      padPrescriptionMatMeds.forEach(matMed => {
        const { name, matMedId: itemId, quantity: amountMatMed } = matMed;

        const matMedToAdd = {
          name,
          amountMatMed,
          itemId
        };

        this._processInsert(matMedToAdd, matMedDetails);
      });
  }
  _processInsert(matMed, matMedDetails) {
    const { once, periodInMinutes, frequency } = matMedDetails;

    const { name, amountMatMed, itemId } = matMed;

    const amount = this._calculateAmount(once, amountMatMed, periodInMinutes, frequency);

    this._saveMatMed({ name, amount, itemId });
  }

  // UPDATE
  update(prescription, prevPrescription, padDetails) {
    const { medicamentId, dietId, procedureId } = prescription;

    const isMedicament = !!medicamentId;
    const isDiet = !!dietId;
    const isProcedure = !!procedureId;

    prescription = this._setPeriodInMinutesInThePrescription(prescription, padDetails);

    prevPrescription = this._setPeriodInMinutesInThePrescription(
      prevPrescription,
      padDetails,
      true
    );

    if (isDiet || isMedicament) {
      this._updateFromMedicamentOrDiet(prescription, prevPrescription);
    }

    if (isProcedure) {
      this._updateFromProcedure(prescription, prevPrescription);
    }

    return this;
  }
  _updateFromProcedure(prescription, prevPrescription) {
    const { padPrescriptionMatMeds, kits, once, periodInMinutes, procedureId } = prescription;

    const {
      padPrescriptionMatMeds: prevPadPrescriptionMatMeds,
      kits: prevKits,
      once: prevOnce,
      prevPeriodInMinutes,
      procedureId: prevProcedureId
    } = prevPrescription;

    const frequency = this._getFrequency(prescription);
    const prevFrequency = this._getFrequency(prevPrescription);

    const currentMatMedDetails = {
      periodInMinutes,
      frequency,
      once
    };
    const prevMatMedDetails = {
      prevPeriodInMinutes,
      prevFrequency,
      prevOnce
    };

    if (prevProcedureId !== procedureId) {
      prevPrescription.periodInMinutes = prevPeriodInMinutes;
      this._removeFromProcedure(prevPrescription);
      this._insertFromProcedure(prescription);
    } else {
      const matMedsFromPrevKits = prevKits
        .map(kit =>
          kit.matMeds.map(matMed => {
            matMed.itemId = matMed.id;
            matMed.amountMatMed = matMed.amount;
            return matMed;
          })
        )
        .flat(1);

      const matMedsFromPrevPadPrescriptionMatMeds = prevPadPrescriptionMatMeds.map(matMed => {
        matMed.itemId = matMed.matMedId;
        matMed.amountMatMed = matMed.quantity;
        return matMed;
      });

      !!kits &&
        kits.forEach(({ matMeds }) =>
          matMeds.forEach(matMed => {
            matMed.itemId = matMed.id;
            matMed.amountMatMed = matMed.amount;
            prevMatMedDetails.prevMatMeds = matMedsFromPrevKits;

            this._processUpdate(matMed, currentMatMedDetails, prevMatMedDetails);
          })
        );

      !!padPrescriptionMatMeds &&
        padPrescriptionMatMeds.forEach(matMed => {
          matMed.itemId = matMed.matMedId;
          matMed.amountMatMed = matMed.quantity;
          prevMatMedDetails.prevMatMeds = matMedsFromPrevPadPrescriptionMatMeds;

          this._processUpdate(matMed, currentMatMedDetails, prevMatMedDetails);
        });

      // remove items
      matMedsFromPrevPadPrescriptionMatMeds
        .filter(
          prevMatMed =>
            !padPrescriptionMatMeds.some(matMed => prevMatMed.matMedId === matMed.matMedId)
        )
        .forEach(matMedToRemove => {
          const matMedDetails = {
            once: prevOnce,
            periodInMinutes: prevPeriodInMinutes,
            frequency: prevFrequency,
            amountMatMed: matMedToRemove.quantity
          };

          this._processRemove(matMedToRemove.matMedId, matMedDetails);
        });
    }
  }
  _updateFromMedicamentOrDiet(prescription, prevPrescription) {
    const {
      medicamentId,
      medicament,
      diet,
      dietId,
      once,
      periodInMinutes,
      dose: amountMatMed
    } = prescription;

    const {
      medicamentId: prevMedicamentId,
      dietId: prevDietId,
      dose: prevAmountMatMed,
      once: prevOnce,
      prevPeriodInMinutes
    } = prevPrescription;

    const itemId = medicamentId || dietId;
    const frequency = this._getFrequency(prescription);
    const name = medicamentId ? medicament.name : diet.name;

    const prevFrequency = this._getFrequency(prevPrescription);
    const prevItemId = prevMedicamentId || prevDietId;

    const currentMatMedDetails = {
      periodInMinutes,
      frequency,
      once
    };

    const prevMatMedDetails = {
      prevPeriodInMinutes,
      prevFrequency,
      prevOnce
    };

    prevPrescription.itemId = prevItemId;
    prevPrescription.amountMatMed = prevAmountMatMed;

    prevMatMedDetails.prevMatMeds = [prevPrescription];

    const matMed = { name, itemId, amountMatMed };

    if (itemId === prevItemId) {
      this._processUpdate(matMed, currentMatMedDetails, prevMatMedDetails);
    } else {
      prevPrescription.periodInMinutes = prevPeriodInMinutes;

      this._removeFromMedicamentOrDiet(prevPrescription);
      this._insertFromMedicamentOrDiet(prescription);
    }
  }
  _processUpdate(currentMatMed, currentMatMedDetails, prevMatMedDetails) {
    const { name, itemId, amountMatMed } = currentMatMed;

    const { periodInMinutes, frequency, once } = currentMatMedDetails;

    const { prevPeriodInMinutes, prevFrequency, prevOnce, prevMatMeds } = prevMatMedDetails;

    const prevMatMed = this._findOneBy({ itemId }, prevMatMeds);

    const amount = this._calculateAmount(once, amountMatMed, periodInMinutes, frequency);

    let currentAmount;

    if (prevMatMed) {
      const prevAmount = this._calculateAmount(
        prevOnce,
        prevMatMed.amountMatMed,
        prevPeriodInMinutes,
        prevFrequency
      );

      currentAmount = amount - prevAmount;
    } else {
      currentAmount = amount;
    }

    this._saveMatMed({ name, amount: currentAmount, itemId });
  }

  // REMOVE
  remove(prescription, padDetails) {
    const { medicamentId, dietId, procedureId } = prescription;
    const isMedicament = !!medicamentId;
    const isDiet = !!dietId;
    const isProcedure = !!procedureId;

    prescription = this._setPeriodInMinutesInThePrescription(prescription, padDetails);

    if (isDiet || isMedicament) {
      this._removeFromMedicamentOrDiet(prescription);
    }

    if (isProcedure) {
      this._removeFromProcedure(prescription);
    }

    return this;
  }
  _removeFromProcedure(prescription) {
    const { once, periodInMinutes, kits, padPrescriptionMatMeds } = prescription;

    const frequency = this._getFrequency(prescription);

    const prevMatMedDetails = {
      once,
      periodInMinutes,
      frequency
    };

    !!kits &&
      kits.forEach(({ matMeds }) =>
        matMeds.forEach(matMed => {
          const itemId = matMed.id;
          prevMatMedDetails.amountMatMed = matMed.amount;

          this._processRemove(itemId, prevMatMedDetails);
        })
      );

    !!padPrescriptionMatMeds &&
      padPrescriptionMatMeds.forEach(matMed => {
        const itemId = matMed.matMedId;
        prevMatMedDetails.amountMatMed = matMed.quantity;

        this._processRemove(itemId, prevMatMedDetails);
      });
  }
  _removeFromMedicamentOrDiet(prescription) {
    const { once, periodInMinutes, dose: amountMatMed, medicamentId, dietId } = prescription;

    const itemId = medicamentId || dietId;

    const frequency = this._getFrequency(prescription);

    const matMedDetails = {
      once,
      periodInMinutes,
      frequency,
      amountMatMed
    };

    this._processRemove(itemId, matMedDetails);
  }
  _processRemove(itemId, matMedDetails) {
    const { once, periodInMinutes, frequency, amountMatMed } = matMedDetails;

    const matMedFinded = this._findOneBy({ itemId }, this._screenData);

    const amountToRemove = this._calculateAmount(once, amountMatMed, periodInMinutes, frequency);

    const amount = -amountToRemove;

    this._saveMatMed({ itemId, amount, name: matMedFinded.name });
  }

  // UTILS
  _saveMatMed(matMed) {
    const { name, itemId, amount } = matMed;

    const matMedFinded = this._findOneBy({ itemId }, this._screenData);
    if (matMedFinded) {
      matMedFinded.amount += amount;

      super.update(matMedFinded.code, matMedFinded);

      const matMedUpdated = this._findOneBy({ code: matMedFinded.code }, this._screenData);

      if (matMedUpdated.amount <= 0) {
        if (!matMedUpdated.id) {
          super.remove(matMedUpdated.code);
        } else if (matMedUpdated.status === STATUS.ACTIVE) {
          super.update(matMedFinded.code, { status: STATUS.INACTIVE });
        }
      } else if (matMedUpdated.status === STATUS.INACTIVE) {
        super.update(matMedFinded.code, { status: STATUS.ACTIVE });
      }
    } else {
      const newMatMed = this._formatToInsert({ name, itemId, amount });

      super.insert(newMatMed, { amount });
    }
  }
  _formatToInsert(matMed) {
    const { name, itemId } = matMed;

    return {
      name,
      itemId,
      code: codeGeneretor(),
      type: PAD_ITEM_TYPE.MAT_MED,
      status: STATUS.ACTIVE
    };
  }
  _calculateAmount(once, amountMatMed, periodInMinutes, frequency) {
    periodInMinutes = parseFloat(periodInMinutes);
    amountMatMed = parseFloat(amountMatMed);
    frequency = parseFloat(frequency);

    let amount;

    if (once) {
      amount = amountMatMed;
    } else {
      amount = Math.floor(periodInMinutes / frequency) * amountMatMed;
    }
    return amount;
  }
  _setPeriodInMinutesInThePrescription = (prescription, padDetails, isPrev = false) => {
    const { endDate, startDate } = padDetails;

    const differenceDays = differenceInDays(new Date(endDate), new Date(startDate));
    let { continuousUse, period } = prescription;

    period = continuousUse ? differenceDays : period;
    const periodInMinutes = 1440 * period;

    if (isPrev) {
      prescription.prevPeriodInMinutes = periodInMinutes;
    } else {
      prescription.periodInMinutes = periodInMinutes;
    }

    return prescription;
  };
  _getFrequency(prescription) {
    const { frequencyAmount, frequencyId, frequency } = prescription;

    if (frequency && typeof frequency === 'number') {
      return frequency;
    }

    return toMinutes(frequencyAmount, frequencyId);
  }
}
